// Book a job (office-booking prototype, technical design §6.5). Only the office books; every booking
// creates the job, its first visit and its draft invoice together (jobs.book). A booking from a call
// keeps the call on the job. Technicians picked here are a suggestion the dispatcher can move; an
// overlap is shown, not blocked. A confirmation text goes only to a phone that agreed to texts.

import {
  confirmationText,
  emptyDraft,
  todayPassed,
  windowLabel,
  windowsFor,
  fromCallWords,
  summaryFromNotes,
  hoursWords,
  lastBookDay,
  weekPageOf,
  type WindowCap,
  dropTag,
  pickTag,
  tagOptions,
  tagTo,
  type BookTag,
  draftHasInput,
} from './book-model.ts';
import { openJob } from './job/job-page.ts';
import { BookWeek } from './BookWeek.tsx';
import { SaveButton } from './shared/SaveButton.tsx';
import { MIRROR_LINE } from './shared/mirror.ts';
import { NewCustomerForm } from './shared/NewCustomer.tsx';
import { termsAt, termsText } from './shared/terms.ts';
import { BookingNotes } from './records/BookingNotes.tsx';
import { formFromSearch } from './shared/new-customer-model.ts';
import './book.css';
import { Header, type NavProps } from './Nav.tsx';
import { useDesign } from './blueprint/design.ts';
import './blueprint/schedule.css';
import { phoneText, type BookStart, type Customer, type visitTextTo } from './types.ts';

import { useBookForm, useCapacityAdjust, useTagList, type Booked } from './book/use-book-form.ts';
export type { Booked };

const LEDE = 'Who it’s for, what’s wrong and when to arrive; the job goes straight onto the dispatch board.';
// Blueprint names the page as its rail does, and its board as the day board (pages/ux-full schedule/book)
const BP_LEDE = 'Pick who it’s for, say what’s wrong, then pick when. It goes straight onto the day board.';

export function BookScreen({
  nav,
  start,
  timeZone,
  onBoard,
}: {
  nav: NavProps;
  start: BookStart | null;
  timeZone: string;
  /** To the board; from a booking, on its day with the visit open (QA-768). */
  onBoard: (at?: { date: string; visit: string }) => void;
}) {
  const bp = useDesign() === 'blueprint';
  return (
    <div className={bp ? 'app bp-sch' : 'app'}>
      <Header nav={nav} title={bp ? 'Book a visit' : 'Book a job'} lede={bp ? BP_LEDE : LEDE} />
      <main className="page book-page">
        <BookForm nav={nav} start={start} timeZone={timeZone} onBoard={onBoard} />
      </main>
    </div>
  );
}

/**
 * The booking form, on the Book screen and in place on a call (BL-101): one form, so every rule is the
 * same wherever it books (after hours, a collections hold, capacity, the confirmation text, the call
 * kept on the job). With `onBooked` the caller says what was booked; without it, the form does.
 */
export function BookForm({
  nav,
  start,
  timeZone,
  onBoard,
  onBooked,
}: {
  nav: NavProps;
  start: BookStart | null;
  timeZone: string;
  /** To the board; from a booking, on its day with the visit open (QA-768). */
  onBoard: (at?: { date: string; visit: string }) => void;
  onBooked?: (b: Booked) => void;
}) {
  // what it does is use-book-form.ts's, shared with Blueprint's Book a visit (BL-189)
  const {
    today,
    nowH,
    mirroring,
    opens,
    plain,
    restored,
    d,
    setD,
    customer,
    setCustomer,
    query,
    setQuery,
    bad,
    setBad,
    found,
    units,
    types,
    typesRead,
    techs,
    page,
    setPage,
    water,
    setCapTry,
    newLoc,
    setNewLoc,
    estimate,
    setEstimate,
    soldAt,
    booked,
    setBooked,
    textingOff,
    error,
    setError,
    dupe,
    busy,
    adding,
    addError,
    newCustomer,
    setNewCustomer,
    customerField,
    patch,
    hold,
    setHold,
    pickCustomer,
    unitTypes,
    wet,
    waterSaid,
    answerWater,
    mayAddTypes,
    clashes,
    readOf,
    grids,
    soon,
    chosenCap,
    capWarn,
    pickCell,
    mayAdjust,
    textTo,
    signedBy,
    stops,
    dayLabel,
    addLocation,
    book,
    invalid,
  } = useBookForm({ nav, start, timeZone, onBooked });
  if (mirroring)
    return (
      <section className="stone card">
        <p>{MIRROR_LINE}</p>
        <div className="row">
          <button className="btn" onClick={() => onBoard()}>
            See the dispatch board
          </button>
        </div>
      </section>
    );

  if (booked)
    return (
      <div className="book-form">
        <div className="notice ok" role="status">
          <b>Booked #{booked.jobNumber}.</b> It’s on the dispatch board
          {estimate ? `, with the work sold on estimate #${estimate.number}` : ''}
          {booked.texted ? ', and the confirmation text is on its way' : ''}.
        </div>
        <section className="stone card">
          <h2 className="h3">
            #{booked.jobNumber} · {types.find((t) => t.id === d.jobTypeId)?.name}
          </h2>
          <div className="ink2">
            {customer?.displayName} · {dayLabel}, arriving {d.window && windowLabel(d.window)}
          </div>
          <p>{d.summary}</p>
          {!booked.texted && (
            <p className="muted small">
              {textingOff
                ? 'Not sent: texting isn’t set up for your company yet.'
                : `No confirmation text${textTo && 'reason' in textTo ? `: ${textTo.reason}` : ''}.`}
            </p>
          )}
          <div className="row">
            {/* on its day, the visit open and in view, not the board where it was last left (QA-768) */}
            <button
              className="btn primary"
              onClick={() => onBoard({ date: d.date, visit: booked.appointmentId })}
            >
              See it on the dispatch board
            </button>
            <button
              className="btn"
              onClick={() => {
                setBooked(null);
                setEstimate(null);
                setCustomer(null);
                setQuery('');
                setD({ ...emptyDraft(opens), businessUnitId: d.businessUnitId });
                setPage(0);
              }}
            >
              Book another
            </button>
          </div>
        </section>
      </div>
    );

  return (
    <div className="book-form">
      {restored && plain && draftHasInput(d, query) && (
        <div className="notice row" role="status">
          <span>Your unsent booking is kept from before; nothing is booked yet.</span>
          <button
            className="btn quiet small"
            onClick={() => {
              setCustomer(null);
              setHold(null);
              setQuery('');
              setNewCustomer(null);
              setBad(null);
              setError(null);
              setD({ ...emptyDraft(opens), businessUnitId: units[0]?.id ?? null });
              setPage(0);
            }}
          >
            Start over
          </button>
        </div>
      )}
      {estimate && (
        <div className="notice">
          Booking the work sold on estimate #{estimate.number}. Its lines go on the new job’s invoice, at the
          address it was sold for, and it leaves To book.
        </div>
      )}
      {start?.lead && d.leadId === start.lead.id && (
        <div className="notice">
          Booking the lead from{' '}
          {start.lead.name ?? (start.lead.phone ? phoneText(start.lead.phone) : 'this request')}. Once it’s
          booked, the lead leaves To book.
        </div>
      )}
      {d.callId && (
        <div className="notice">
          Linked to the call. The job keeps it, so its timeline starts with the call.
          {fromCallWords(!!start?.locationId, !!summaryFromNotes(start?.notes)) &&
            ` ${fromCallWords(!!start?.locationId, !!summaryFromNotes(start?.notes))}`}
        </div>
      )}
      <div className="grid2">
        <section className="stone card">
          <h2 className="h4">Who and where</h2>
          <label className="f">
            Customer (type a name, number or street)
            <input
              ref={customerField}
              className="field"
              {...invalid('customer')}
              value={query}
              onChange={(e) => {
                setQuery(e.target.value);
                if (customer && e.target.value !== customer.displayName) {
                  setCustomer(null);
                  setHold(null);
                  patch({ customerId: null, locationId: null });
                }
              }}
              autoComplete="off"
              readOnly={!!estimate}
              role="combobox"
              aria-expanded={found.length > 0}
              aria-controls="cust-found"
            />
          </label>
          {found.length > 0 && !newCustomer && (
            <ul className="found" id="cust-found" role="listbox">
              {found.map((c) => (
                <li key={c.id} role="option" aria-selected={false}>
                  <button className="row-btn" onClick={() => void pickCustomer(c.id)}>
                    <b>{c.displayName}</b>
                    {c.city && <span className="ink2"> · {c.city}</span>}
                  </button>
                </li>
              ))}
            </ul>
          )}
          {!customer && !estimate && !newCustomer && (
            <div className="row">
              <button className="btn soft" onClick={() => setNewCustomer(formFromSearch(query))}>
                New customer
              </button>
            </div>
          )}
          {newCustomer && (
            <div className="new-customer" role="group" aria-label="New customer">
              <h3 className="h4">New customer</h3>
              <NewCustomerForm
                start={newCustomer}
                consentNote="Consent (saved with who and when)"
                submitLabel="Add customer"
                onDone={(id) => {
                  setNewCustomer(null);
                  void pickCustomer(id).then(() => customerField.current?.focus());
                }}
                onCancel={() => {
                  setNewCustomer(null);
                  customerField.current?.focus();
                }}
              />
            </div>
          )}
          {customer && hold && (
            <p className="notice warn" role="status">
              <b>On a collections hold</b>
              {hold.reason ? `: ${hold.reason}` : ''}. Emergency work goes ahead; check with whoever keeps the
              books before booking anything else.
            </p>
          )}
          {customer && estimate && (
            <>
              <p className="billsto">
                Address: <b>{soldAt ?? 'Loading…'}</b>{' '}
                <span className="muted">(where the estimate was sold)</span>
              </p>
              <BillsTo customer={customer} locationId={d.locationId} />
              {textTo?.siteContact && <TextsGoTo to={textTo} />}
              <BookingNotes locationId={d.locationId} />
            </>
          )}
          {customer && !estimate && (
            <>
              <label className="f">
                Location
                <select
                  className="field"
                  {...invalid('location')}
                  value={d.locationId ?? ''}
                  onChange={(e) => patch({ locationId: e.target.value })}
                >
                  {customer.locations.map((l) => (
                    <option key={l.id} value={l.id}>
                      {l.line1}, {l.city}
                    </option>
                  ))}
                </select>
              </label>
              <BillsTo customer={customer} locationId={d.locationId} />
              {textTo?.siteContact && <TextsGoTo to={textTo} />}
              <BookingNotes locationId={d.locationId} />
              {(() => {
                // The customer's PO, when they give one now (F-29): never required to book; Review asks.
                const t = termsAt(customer, d.locationId);
                return (
                  <label className="f">
                    {t.terms?.poRequired
                      ? `Customer’s PO number (${t.name} requires one)`
                      : 'Customer’s PO number'}
                    <input
                      className="field"
                      {...invalid('po')}
                      value={d.poNumber}
                      maxLength={60}
                      onChange={(e) => patch({ poNumber: e.target.value })}
                      placeholder={t.terms?.poRequired ? 'or add it before the job is reviewed' : 'optional'}
                      autoComplete="off"
                    />
                  </label>
                );
              })()}
              {newLoc ? (
                <div className="fgrid">
                  <label className="f">
                    Street
                    <input
                      className="field"
                      value={newLoc.line1}
                      onChange={(e) => setNewLoc({ ...newLoc, line1: e.target.value })}
                    />
                  </label>
                  <label className="f">
                    City
                    <input
                      className="field"
                      value={newLoc.city}
                      onChange={(e) => setNewLoc({ ...newLoc, city: e.target.value })}
                    />
                  </label>
                  <div className="row">
                    <SaveButton
                      className="btn soft"
                      busy={adding}
                      busyLabel="Adding…"
                      error={addError}
                      disabled={!newLoc.line1.trim() || !newLoc.city.trim()}
                      onClick={() => void addLocation()}
                    >
                      Add this location
                    </SaveButton>
                    <button className="btn quiet" onClick={() => setNewLoc(null)}>
                      Cancel
                    </button>
                  </div>
                </div>
              ) : (
                <button className="btn quiet" onClick={() => setNewLoc({ line1: '', city: '' })}>
                  Add location
                </button>
              )}
            </>
          )}

          <h3 className="h4">What</h3>
          <div className="fgrid">
            {/* a company with one department has nothing to choose here (QA-346) */}
            {units.length > 1 && (
              <label className="f">
                Department
                <select
                  className="field"
                  {...invalid('department')}
                  value={d.businessUnitId ?? ''}
                  onChange={(e) => patch({ businessUnitId: e.target.value, jobTypeId: null })}
                >
                  {units.map((u) => (
                    <option key={u.id} value={u.id}>
                      {u.name}
                    </option>
                  ))}
                </select>
              </label>
            )}
            <label className="f">
              Job type
              <select
                className="field"
                {...invalid('jobType')}
                {...(typesRead && !types.length ? { 'aria-describedby': 'book-no-types' } : {})}
                value={d.jobTypeId ?? ''}
                onChange={(e) => patch({ jobTypeId: e.target.value || null })}
              >
                <option value="">{typesRead && !types.length ? 'None yet' : 'Pick one'}</option>
                {unitTypes.map((t) => (
                  <option key={t.id} value={t.id}>
                    {t.name}
                  </option>
                ))}
              </select>
            </label>
          </div>
          {/* QA-723: with none, say so and where they're added; what's filled in here is kept */}
          {typesRead && !types.length && (
            <p className="notice warn small" id="book-no-types">
              No job types yet.{' '}
              {mayAddTypes ? (
                <>
                  Add one in <a href="#/settings/org">Setup › Job types</a>, then come back: what you’ve
                  filled in here is kept.
                </>
              ) : (
                'The owner or a manager adds them in Setup › Job types.'
              )}
            </p>
          )}
          <label className="f">
            Summary (required: the tech reads this first)
            <textarea
              className="field area"
              {...invalid('summary')}
              value={d.summary}
              placeholder="What is wrong, where, anything the tech should bring"
              onChange={(e) => patch({ summary: e.target.value })}
            />
          </label>
          {wet && (
            // a leak or a burst: is water running now, and is it shut off? It decides how soon
            <div className="bk-water" role="group" aria-label="Water">
              <div className="flabel">Is water running now?</div>
              <div className="pills" role="group" aria-label="Is water running now?">
                <button
                  className="pill"
                  aria-pressed={water.running === true}
                  onClick={() => answerWater({ running: true, shutOff: water.shutOff })}
                >
                  Yes
                </button>
                <button
                  className="pill"
                  aria-pressed={water.running === false}
                  onClick={() => answerWater({ running: false, shutOff: null })}
                >
                  No
                </button>
              </div>
              {water.running && (
                <>
                  <div className="flabel">Is it shut off?</div>
                  <div className="pills" role="group" aria-label="Is it shut off?">
                    <button
                      className="pill"
                      aria-pressed={water.shutOff === true}
                      onClick={() => answerWater({ running: true, shutOff: true })}
                    >
                      Yes
                    </button>
                    <button
                      className="pill"
                      aria-pressed={water.shutOff === false}
                      onClick={() => answerWater({ running: true, shutOff: false })}
                    >
                      No
                    </button>
                  </div>
                </>
              )}
              {waterSaid && (
                <p className="notice small" role="status">
                  {waterSaid}
                </p>
              )}
            </div>
          )}
          <div className="flabel">Visit kind</div>
          <div className="pills" role="group" aria-label="Visit kind" {...invalid('kind')}>
            <button
              className="pill"
              aria-pressed={d.kind === 'estimate_visit'}
              disabled={!!estimate}
              onClick={() => patch({ kind: 'estimate_visit' })}
            >
              Estimate visit (default)
            </button>
            <button
              className="pill"
              aria-pressed={d.kind === 'service'}
              onClick={() => patch({ kind: 'service' })}
            >
              Service (known fix)
            </button>
          </div>
          {/* an estimate's work is booked by estimates.bookSold, which takes no tags */}
          {!estimate && <BookTags customer={customer} tags={d.tags} onChange={(tags) => patch({ tags })} />}
        </section>

        <section className="stone card">
          <h2 className="h4">When and who</h2>
          <label className="checkrow">
            <input
              type="checkbox"
              checked={d.afterHours}
              onChange={(e) => {
                patch({
                  afterHours: e.target.checked,
                  window: null,
                  ...(e.target.checked ? { date: today } : d.date === today ? { date: opens } : {}),
                });
                setPage(0);
              }}
            />{' '}
            After hours or emergency
          </label>
          {d.afterHours && (
            <label className="f">
              Why it can’t wait (required)
              <input
                className="field"
                {...invalid('afterHours')}
                value={d.afterHoursReason}
                placeholder="e.g. active leak, no water, no heat"
                onChange={(e) => patch({ afterHoursReason: e.target.value })}
              />
            </label>
          )}
          {d.afterHours ? (
            <>
              {/* after hours: the evening's windows for a day, today first (no capacity is read for them) */}
              <label className="f bk-jump">
                Day
                <input
                  className="field"
                  type="date"
                  min={today}
                  max={lastBookDay(today)}
                  value={d.date}
                  onChange={(e) =>
                    e.target.value &&
                    weekPageOf(today, e.target.value) !== null &&
                    patch({ date: e.target.value, window: null })
                  }
                />
              </label>
              <div className="flabel">Arrival window</div>
              <div className="pills" role="group" aria-label="Arrival window">
                {windowsFor(true, d.date, today, nowH).map(({ window: w, label, started }) => (
                  <button
                    key={w[0]}
                    className="pill"
                    aria-pressed={d.window?.[0] === w[0]}
                    disabled={started}
                    onClick={() => patch({ window: w })}
                  >
                    {label}
                  </button>
                ))}
              </div>
            </>
          ) : (
            <>
              <div className="flabel">
                Arrival window{bad === 'window' && <span className="error small"> · check it</span>}
              </div>
              <BookWeek
                today={today}
                last={lastBookDay(today)}
                page={page}
                grid={grids[page]}
                failed={readOf(page) === null}
                chosen={{ date: d.date, window: d.window }}
                soonest={soon}
                length={(readOf(page) ?? readOf(0))?.jobType?.durationMinutes ?? null}
                onPage={setPage}
                onPick={pickCell}
                onJump={(date) => {
                  patch({ date, window: null });
                  setPage(weekPageOf(today, date) ?? 0);
                }}
              />
              {todayPassed(nowH) && (
                <p className="muted small">
                  Today’s windows have passed. For tonight, tick After hours or emergency.
                </p>
              )}
            </>
          )}
          {capWarn && <p className="notice warn small">{capWarn}</p>}
          {mayAdjust && chosenCap && d.window && (
            <CapacityAdjust
              key={`${d.date}:${d.window[0]}`}
              date={d.date}
              windowStartHour={d.window[0]}
              window={chosenCap}
              businessUnitId={d.businessUnitId}
              onChanged={() => setCapTry((n) => n + 1)}
            />
          )}
          <div className="flabel">Technicians (a suggestion; the dispatcher can move it)</div>
          <div className="pills" role="group" aria-label="Technicians" {...invalid('techs')}>
            {techs.map((t) => {
              const on = d.techIds.includes(t.id);
              // free in the chosen window (BL-057), said as the chip's description
              const free = !!chosenCap?.freeTechIds.includes(t.id);
              return (
                <button
                  key={t.id}
                  className="pill"
                  aria-pressed={on}
                  {...(free ? { 'aria-describedby': `free-${t.id}` } : {})}
                  onClick={() =>
                    patch({ techIds: on ? d.techIds.filter((x) => x !== t.id) : [...d.techIds, t.id] })
                  }
                >
                  {t.name}
                  {free && (
                    <small className="cap" id={`free-${t.id}`} aria-hidden="true">
                      free then
                    </small>
                  )}
                </button>
              );
            })}
          </div>
          {!d.techIds.length && <p className="muted small">No tech yet: it waits on the dispatch board.</p>}
          {clashes.length > 0 && (
            <div className="notice warn">
              Overlap: {clashes.map((c) => `${c.techName} already has #${c.jobNumber}`).join('; ')}. You can
              still book; the dispatcher will see both.
            </div>
          )}

          {customer &&
            (textTo && 'method' in textTo ? (
              <>
                <label className="checkrow">
                  <input
                    type="checkbox"
                    checked={d.sendConfirmation}
                    onChange={(e) => patch({ sendConfirmation: e.target.checked })}
                  />{' '}
                  Text {textTo.firstName} a confirmation
                </label>
                {d.sendConfirmation && d.window && (
                  <div className="sms">
                    {confirmationText({
                      firstName: textTo.firstName,
                      company: signedBy,
                      dayLabel,
                      window: d.window,
                    })}
                  </div>
                )}
              </>
            ) : (
              <p className="muted small">
                No confirmation text: {textTo && 'reason' in textTo ? textTo.reason : ''}.
              </p>
            ))}

          <div className="row">
            <SaveButton
              busy={busy}
              busyLabel="Booking…"
              error={error}
              errorId="book-refused"
              disabled={stops.length > 0 || !!dupe}
              onClick={() => void book()}
            >
              Book {d.kind === 'estimate_visit' ? 'estimate visit' : 'service job'}
            </SaveButton>
          </div>
          {dupe && (
            <div className="notice warn" role="alert">
              <p>{dupe.message}</p>
              <div className="row">
                <button className="btn" disabled={busy} onClick={() => void book(true)}>
                  Book anyway
                </button>
                <button className="btn quiet" onClick={() => openJob(dupe.jobId)}>
                  Open #{dupe.jobNumber}
                </button>
              </div>
            </div>
          )}
          {stops.length > 0 && <p className="muted small">To book: {stops.join(', ')}.</p>}
        </section>
      </div>
    </div>
  );
}

/**
 * Who hears about this visit (F-54), when the address has a site contact: "Texts go to Dee Alvarez
 * (site contact)", or why none will go. Never the customer instead.
 */
function TextsGoTo({ to }: { to: NonNullable<ReturnType<typeof visitTextTo>> }) {
  return 'method' in to ? (
    <p className="billsto">
      Texts go to <b>{to.siteContact}</b> <span className="muted">(site contact)</span>
    </p>
  ) : (
    <p className="billsto">
      <span className="chip say warn">No visit texts</span> {to.reason[0]!.toUpperCase() + to.reason.slice(1)}
      .
    </p>
  );
}

/**
 * Who the invoice goes to (F-54, QA-253), a property manager or strata or the customer, and their
 * terms (F-29): "Bills to: Harbourview Strata (for Okafor family) · Net 30 · PO required".
 */
function BillsTo({ customer, locationId }: { customer: Customer; locationId: string | null }) {
  const t = termsAt(customer, locationId);
  const words = termsText(t.terms);
  return (
    <p className="billsto">
      Bills to: <b>{t.name}</b>
      {t.billTo && <span className="muted"> (for {customer.displayName})</span>}
      {words && <span> · {words}</span>}
    </p>
  );
}

/**
 * The office's own change to the chosen window (BL-057 phase 2), for those who dispatch: close it for
 * the day, or add on-call time, with a reason; or take back a change made. The department's change,
 * when one is picked; Book re-reads the window after.
 */
function CapacityAdjust({
  date,
  windowStartHour,
  window,
  businessUnitId,
  onChanged,
}: {
  date: string;
  windowStartHour: number;
  window: WindowCap;
  businessUnitId: string | null;
  onChanged: () => void;
}) {
  const {
    open,
    setOpen,
    kind,
    setKind,
    minutes,
    setMinutes,
    reason,
    setReason,
    busy,
    error,
    made,
    undo,
    apply,
  } = useCapacityAdjust({ date, windowStartHour, window, businessUnitId, onChanged });
  return (
    <div className="cap-adjust" role="group" aria-label={`Capacity of ${window.label}`}>
      {made.map((a) => (
        <p key={a.id} className="muted small row">
          <span>
            {a.closed
              ? 'Closed'
              : a.minutes > 0
                ? `${hoursWords(a.minutes)} added`
                : `${hoursWords(-a.minutes)} taken off`}
            : {a.reason}
          </span>{' '}
          <button className="btn quiet small" disabled={busy} onClick={() => undo(a.id)}>
            Undo
          </button>
        </p>
      ))}
      {!open ? (
        <button className="btn quiet small" onClick={() => setOpen(true)}>
          Change {window.label}’s capacity
        </button>
      ) : (
        <div className="cap-form">
          <div className="pills" role="group" aria-label="Change">
            <button className="pill" aria-pressed={kind === 'close'} onClick={() => setKind('close')}>
              Close it for the day
            </button>
            <button className="pill" aria-pressed={kind === 'add'} onClick={() => setKind('add')}>
              Add on-call time
            </button>
          </div>
          {kind === 'add' && (
            <label className="f">
              How long
              <select className="field" value={minutes} onChange={(e) => setMinutes(Number(e.target.value))}>
                {[30, 60, 90, 120].map((m) => (
                  <option key={m} value={m}>
                    {hoursWords(m)}
                  </option>
                ))}
              </select>
            </label>
          )}
          <label className="f">
            Why
            <input
              className="field"
              value={reason}
              maxLength={500}
              onChange={(e) => setReason(e.target.value)}
            />
          </label>
          {error && <p className="error small">{error}</p>}
          <div className="row">
            <button className="btn soft" disabled={busy || !reason.trim()} onClick={apply}>
              {kind === 'close' ? 'Close it' : 'Add it'}
            </button>
            <button className="btn quiet" onClick={() => setOpen(false)}>
              Cancel
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

/**
 * BL-129: tags when booking. Each picked tag is saved to this job, and, ticked, to the customer too, as
 * their page puts one on (jobs.book's customerTagIds: refused to someone who can't edit customers).
 */
function BookTags({
  customer,
  tags,
  onChange,
}: {
  customer: Customer | null;
  tags: BookTag[];
  onChange: (tags: BookTag[]) => void;
}) {
  const { all, failed } = useTagList();
  const offered = all ? tagOptions(all, tags) : [];
  const has = customer?.tags ?? [];
  return (
    <>
      <label className="f">
        Tags
        <select
          className="field"
          value=""
          disabled={!all || !offered.length}
          onChange={(e) => {
            const t = offered.find((x) => x.id === e.target.value);
            if (t) onChange(pickTag(tags, t));
          }}
        >
          <option value="">
            {failed
              ? 'Couldn’t get the tags'
              : !all
                ? 'Getting the tags…'
                : offered.length
                  ? 'Add a tag'
                  : 'Every tag is picked'}
          </option>
          {offered.map((t) => (
            <option key={t.id} value={t.id}>
              {t.name}
            </option>
          ))}
        </select>
      </label>
      {customer && has.length > 0 && (
        <div className="row">
          <span className="muted small">{customer.displayName} has</span>
          {has.map((t) => (
            <span key={t.id} className="chip">
              {t.name}
            </span>
          ))}
        </div>
      )}
      {tags.map((t) => (
        <div key={t.id} className="row" role="group" aria-label={`Tag ${t.name}`}>
          <span className="chip">{t.name}</span>
          <label className="checkrow">
            <input
              type="checkbox"
              checked={t.job}
              onChange={(e) => onChange(tagTo(tags, t.id, 'job', e.target.checked))}
            />{' '}
            Save to this job
          </label>
          <label className="checkrow">
            <input
              type="checkbox"
              checked={t.customer}
              onChange={(e) => onChange(tagTo(tags, t.id, 'customer', e.target.checked))}
            />{' '}
            Save to the customer
          </label>
          <button type="button" className="btn quiet" onClick={() => onChange(dropTag(tags, t.id))}>
            Remove
          </button>
        </div>
      ))}
    </>
  );
}
